import type { TrackingPageEvent } from "@redotech/order-tracking-common/tracking-page-order-definition";
import { useTranslation } from "@redotech/redo-i18n";
import type { TrackingTextRule } from "@redotech/redo-order-tracking-settings-common/tracking-text-rule";
import { memo, useState } from "react";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import {
  formatEventMessage,
  formatEventTimestamp,
} from "./tracking-details-shared";

export const CollapsibleTimeline = memo(function CollapsibleTimeline({
  events,
  textRules,
  bodyStyles,
  labelStyles,
  linkStyles,
}: {
  events: TrackingPageEvent[];
  textRules?: readonly TrackingTextRule[];
  bodyStyles: React.CSSProperties;
  labelStyles: React.CSSProperties;
  linkStyles: React.CSSProperties;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visibleEvents = expanded ? events : events.slice(0, 1);
  const hiddenCount = events.length - 1;
  const dotColor = bodyStyles.color ?? "currentColor";

  return (
    <Flex dir="column" gap="md">
      {visibleEvents.map((e, i) => {
        const isLast = i === visibleEvents.length - 1;
        const isCollapsedStub = isLast && !expanded && hiddenCount > 0;
        const renderLine = !isLast || isCollapsedStub;
        const isCurrent = i === 0;

        return (
          <Flex
            dir="column"
            gap="xxs"
            key={i}
            style={{
              position: "relative",
              paddingLeft: "var(--redo-spacing-3xl)",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 16,
                height: 16,
                borderRadius: "var(--border-radius-3xl)",
                border: `2px solid ${dotColor}`,
                boxSizing: "border-box",
              }}
            />
            {!isCurrent && (
              <span
                style={{
                  position: "absolute",
                  left: 3,
                  top: 3,
                  width: 10,
                  height: 10,
                  borderRadius: "var(--border-radius-2xl)",
                  backgroundColor: dotColor,
                }}
              />
            )}
            {renderLine && (
              <span
                style={{
                  position: "absolute",
                  left: 7,
                  top: 16,
                  width: 2,
                  height: isCollapsedStub ? 24 : "calc(100% - 8px)",
                  backgroundColor: dotColor,
                  borderRadius: "var(--border-radius-3xl)",
                }}
              />
            )}
            <Text fontSize="xs" fontWeight="semibold" style={bodyStyles}>
              {formatEventTimestamp(e.timestamp, i18n.language)}
            </Text>
            <Text fontSize="xs" style={labelStyles}>
              {formatEventMessage(e, textRules)}
            </Text>
          </Flex>
        );
      })}
      {hiddenCount > 0 && (
        <button
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          style={{
            marginLeft: "var(--redo-spacing-3xl)",
            alignSelf: "flex-start",
            padding: "var(--redo-spacing-none)",
            background: "none",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
          }}
          type="button"
        >
          <Text
            fontSize="xs"
            fontWeight="semibold"
            style={{ ...linkStyles, textDecoration: "underline" }}
          >
            {expanded ? t("Hide tracking history") : t("View tracking history")}
          </Text>
        </button>
      )}
    </Flex>
  );
});
